import Button from 'ext-ui/components/Button/Button'
import ExtUITextInput from 'ext-ui/components/TextInput/TextInput'
import React, { FormEvent, ReactElement, useEffect, useState } from 'react'
import { ExtendCurrency } from 'src/enums'
import { usePrevious } from 'src/hooks'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { toDollars } from 'src/utils/currency.utils'

import SettingsSegmented from '../SettingsSegmented/SettingsSegmented'
import type { CompanyLineSummary } from './SpendLimitTile'

type LimitAdjustSectionProps = {
  /**
   * Renders the "Adjust the spend limit" heading inline. Not used after the
   * flyout conversion (the drawer supplies its own title via
   * `SettingsDrawerShell`), but kept so this section still works standalone.
   */
  showHeading: boolean
  currency: ExtendCurrency
  canApplyDirectly: boolean
  useLimitsApi?: boolean
  /** Current limit in minor units, already resolved for the connected/unconnected path. */
  limitCents?: number
  onApplyPermanentLimit?: (permanentLimitMinorUnits: number) => void
  isApplyingLimit?: boolean
  limitSaveError?: string
  limitSaveSucceeded?: boolean
  companyLine?: CompanyLineSummary
  /**
   * The company line's currency, already resolved to `ExtendCurrency` by the
   * caller. `companyLine.currency` itself is a bare wire string — nothing
   * promises it shares the card's typed currency, so callers cast it once
   * rather than every consumer re-deriving the same fallback.
   */
  companyLineCurrency: ExtendCurrency
}

type LimitKind = 'permanent' | 'temporary'

/**
 * The form, its governance routing (instant-apply vs "submit request"), and
 * the pending/applied states. Read-only summary figures live in
 * `LimitSummary` instead — this section only ever mutates.
 */
export default function LimitAdjustSection(
  props: LimitAdjustSectionProps,
): ReactElement {
  const {
    showHeading,
    currency,
    canApplyDirectly,
    useLimitsApi = false,
    limitCents,
    onApplyPermanentLimit,
    isApplyingLimit = false,
    limitSaveError,
    limitSaveSucceeded = false,
    companyLine,
    companyLineCurrency,
  } = props
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()

  const [limitKind, setLimitKind] = useState<LimitKind>('permanent')
  const [limitAmount, setLimitAmount] = useState<number | undefined>(undefined)
  const [limitFocused, setLimitFocused] = useState(false)
  const [reason, setReason] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [amountError, setAmountError] = useState('')
  const [datesError, setDatesError] = useState('')

  // Show a formatted amount ($60,000) at rest and raw digits while editing —
  // the monetary-input pattern used across the app (see AmountInputField).
  const limitDisplayValue =
    limitAmount === undefined
      ? ''
      : limitFocused
        ? String(limitAmount)
        : formatCurrency({
            amount: limitAmount,
            currency,
            maximumFractionDigits: 0,
          })

  // `limitSaveSucceeded` is the mutation's own `isSuccess`, which stays true
  // for the life of the mutation — so without this the "Saved" note sits beside
  // the button while the user types a DIFFERENT amount, claiming a value that
  // was never sent.
  const [editedSinceSave, setEditedSinceSave] = useState(false)
  const showSavedNote = useLimitsApi && limitSaveSucceeded && !editedSinceSave

  /**
   * Clear the form once a connected save lands.
   *
   * The authoritative new figure is already rendered in the "Card limit" row
   * in `LimitSummary`, so leaving the submitted amount sitting in the input
   * duplicates it and — worse — leaves a filled, operable form whose obvious
   * next action is to re-submit the value that was just applied. Clearing
   * leaves "Saved" beside an empty form, which reads as "your change landed"
   * rather than "here is a pending change".
   *
   * Keyed on the isApplying → settled TRANSITION, not on `limitSaveSucceeded`
   * alone: that flag stays true for the life of the mutation, so depending on
   * it directly would wipe the field again on every later render, including
   * mid-typing.
   */
  const previouslyApplying = usePrevious(isApplyingLimit)
  useEffect(() => {
    if (previouslyApplying === true && !isApplyingLimit && limitSaveSucceeded) {
      setLimitAmount(undefined)
      setLimitFocused(false)
      setReason('')
      setAmountError('')
      // Not an edit — the note must survive this reset and clear on the user's
      // next keystroke instead.
      setEditedSinceSave(false)
    }
  }, [previouslyApplying, isApplyingLimit, limitSaveSucceeded])

  const handleLimitChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const digits = event.target.value.replace(/[^0-9]/g, '')
    setLimitAmount(digits === '' ? undefined : Number(digits))
    setAmountError('')
    setEditedSinceSave(true)
  }

  /**
   * What the submit button honestly promises.
   *
   * A DECREASE applies instantly for every role — there is nothing to approve
   * when a card asks for less headroom. The flat
   * `canApplyDirectly ? 'Update limit' : 'Submit request'` therefore told a Card
   * Manager lowering their own limit that they were filing a request, and then
   * applied it immediately. The design guards this explicitly: "the button never
   * promises 'Submit request' for a decrease, which now applies instantly."
   *
   * Compared in dollars on both sides — `limitAmount` is what the user typed,
   * `limitCents` is the snapshot in minor units.
   */
  const currentLimitDollars =
    limitCents !== undefined ? toDollars(limitCents) : undefined
  const isDecrease =
    limitAmount !== undefined &&
    currentLimitDollars !== undefined &&
    limitAmount < currentLimitDollars
  // On the CONNECTED path every permitted change is instant: the limits
  // contract has no approval routing at all — `LimitChangeResponse.status` is
  // `SUCCEEDED` only — so holding APPLY_PHYSICAL_CARD_LIMIT_CHANGE means the
  // POST applies immediately regardless of role. EX-40509 makes this explicit:
  // "Do not present this permanent direct-change flow as 'Submit request', and
  // do not expose the deferred temporary-limit or approval-request path."
  const appliesInstantly = useLimitsApi || canApplyDirectly || isDecrease
  const submitLabel = appliesInstantly
    ? canApplyDirectly
      ? translate('Update limit')
      : translate('Apply')
    : translate('Submit request')

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault()

    const amountIsValid = limitAmount !== undefined && limitAmount > 0
    setAmountError(
      amountIsValid ? '' : translate('Enter a limit greater than zero.'),
    )

    const datesAreValid =
      limitKind === 'permanent' ||
      (startDate !== '' && endDate !== '' && startDate <= endDate)
    setDatesError(
      datesAreValid ? '' : translate('Enter a start and end date, in order.'),
    )

    if (!amountIsValid || !datesAreValid) {
      return
    }

    if (useLimitsApi) {
      // NOT cleared here: the isApplying -> settled effect owns that. Clearing
      // before the call meant a submit the page dropped (no snapshot, so no
      // mutation) re-showed "Saved" beside an amount that was never sent.
      // Minor units at the transport boundary — the contract's money is always
      // an integer in minor units, never a float.
      onApplyPermanentLimit?.(Math.round((limitAmount ?? 0) * 100))
      return
    }
    setSubmitted(true)
  }

  const resetForm = (): void => {
    setSubmitted(false)
    setLimitAmount(undefined)
    setLimitFocused(false)
    setReason('')
    setStartDate('')
    setEndDate('')
    setAmountError('')
    setDatesError('')
  }

  // Temporary limits are deliberately absent on the connected path: the
  // contract supports `kind: "PERMANENT"` only and rejects anything else with
  // CARD-MGMT.8, so offering the control would promise a change the server
  // cannot make.
  const limitKindOptions = [
    { label: translate('Permanent'), value: 'permanent' },
    ...(useLimitsApi
      ? []
      : [{ label: translate('Temporary'), value: 'temporary' }]),
  ]

  return (
    <>
      {showHeading && (
        <h4 className="SpendLimitTile-heading">
          {translate('Adjust the spend limit')}
        </h4>
      )}
      <p className="SpendLimitTile-body">
        {useLimitsApi
          ? // No governed/approval branch on the connected path: nothing is
            // routed for approval, so describing it that way would promise a
            // step the server never performs. The company-line figure is
            // interpolated only where that read resolved AND the row itself is
            // shown, rather than printing a placeholder the user could act on.
            companyLine !== undefined && canApplyDirectly
            ? translate(
                'This card limit is a spend control within your company line. Decreases apply instantly; raising it within your available line (${available}) also applies instantly and is recorded in Activity.',
                {
                  available: formatCurrency({
                    // The LINE's own currency, not the card's — nothing
                    // promises they agree.
                    amount: toDollars(companyLine.availableCredit),
                    currency: companyLineCurrency,
                    maximumFractionDigits: 0,
                  }),
                },
              )
            : translate(
                'This card limit is a spend control within your company line. Decreases apply instantly; raising it within your available line also applies instantly and is recorded in Activity.',
              )
          : canApplyDirectly
            ? translate(
                'Once connected: decreases apply instantly, and increases are applied within your company line and recorded in Activity.',
              )
            : translate(
                'Once connected: decreases apply instantly, and increases are governed — routed to an Admin or Owner to approve, then applied within your company line, and recorded in Activity.',
              )}
      </p>

      {!useLimitsApi && (
        <p className="SpendLimitTile-body" data-testid="spend-limit-preview">
          {translate(
            'Limit changes are not connected yet. This form is a preview — nothing is sent and no limit changes.',
          )}
        </p>
      )}

      {/*
        The loading / error / unavailable / no-permission ladder used to sit
        here. It now renders on the tile itself (`LimitStateNote`), because the
        flyout conversion put this section behind a button — explaining "you
        cannot adjust this" only after the user clicks "Adjust spend limit" is
        one click too late. `SpendLimitTile` gates the button on
        `limitIsAdjustable`, so this section only ever mounts when the form is
        genuinely actionable.
      */}
      {submitted ? (
        <div className="SpendLimitTile-confirmation">
          <span className="SpendLimitTile-confirmationHeading">
            {appliesInstantly
              ? translate('Nothing was applied')
              : translate('Nothing was submitted')}
          </span>
          <p className="SpendLimitTile-confirmationBody">
            {appliesInstantly
              ? translate(
                  'This is a preview of the update flow. No change was made and the card limit is unchanged.',
                )
              : translate(
                  'This is a preview of the request flow. No request was sent and the card limit is unchanged.',
                )}
          </p>
          <Button
            variant="no-style"
            onClick={resetForm}
            data-testid="spend-limit-reset"
          >
            {translate('Start over')}
          </Button>
        </div>
      ) : (
        <form className="SpendLimitTile-form" onSubmit={handleSubmit}>
          <SettingsSegmented
            ariaLabel={translate('Limit duration')}
            options={limitKindOptions}
            value={limitKind}
            onChange={(next) => setLimitKind(next as LimitKind)}
          />
          <ExtUITextInput
            variant="small"
            label={
              limitKind === 'temporary'
                ? translate('Temporary limit')
                : translate('New limit')
            }
            placeholder={limitKind === 'temporary' ? '$75,000' : '$60,000'}
            value={limitDisplayValue}
            inputMode="numeric"
            requirement="required"
            errorText={amountError}
            onFocus={() => setLimitFocused(true)}
            onBlur={() => setLimitFocused(false)}
            onChange={handleLimitChange}
            data-testid="spend-limit-amount"
          />
          {limitKind === 'temporary' && (
            <div className="SpendLimitTile-dates">
              <ExtUITextInput
                variant="small"
                label={translate('Start')}
                type="date"
                requirement="required"
                errorText={datesError}
                value={startDate}
                onChange={(event) => {
                  setStartDate(event.target.value)
                  setDatesError('')
                }}
              />
              <ExtUITextInput
                variant="small"
                label={translate('End')}
                type="date"
                requirement="required"
                value={endDate}
                onChange={(event) => {
                  setEndDate(event.target.value)
                  setDatesError('')
                }}
              />
            </div>
          )}
          <ExtUITextInput
            variant="small"
            label={translate('Reason')}
            placeholder={translate('Q3 travel & events budget')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="SpendLimitTile-formActions">
            <Button
              type="submit"
              dropShadow={false}
              loading={useLimitsApi ? isApplyingLimit : undefined}
              data-testid="spend-limit-submit"
            >
              {submitLabel}
            </Button>
            {showSavedNote && (
              <span
                className="SpendLimitTile-savedNote"
                data-testid="spend-limit-saved"
              >
                {translate('Saved')}
              </span>
            )}
            {useLimitsApi && limitSaveError !== undefined && (
              <span
                className="SpendLimitTile-errorNote"
                role="alert"
                data-testid="spend-limit-save-error"
              >
                {limitSaveError}
              </span>
            )}
          </div>
        </form>
      )}
    </>
  )
}
